import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import SEO from "@/components/SEO";
import { CALENDLY_URL } from "@/content";
import Reveal from "@/components/Reveal";
import { INSIGHTS } from "@/insights";

export default function Insights() {
  const [lead, ...rest] = INSIGHTS;

  return (
    <div data-testid="insights-page">
      <SEO
        title="Insights | Commentary for Commercial Leaders in Technical Markets | Thrive Business Development"
        description="Short commentary on business development, market intelligence and targeting in energy, engineering and technical B2B markets."
      />

      <section className="relative overflow-hidden pt-40 pb-20 sm:pt-48 sm:pb-24" data-testid="insights-hero">
        <div className="absolute inset-0 dot-grid-bg opacity-50" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="eyebrow mb-6">Insights</p>
            <h1 className="font-display max-w-3xl text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-[-0.035em] leading-[1.02] text-white">
              Commentary for Commercial Leaders in Technical Markets
            </h1>
          </Reveal>
          <Reveal delay={0.12}>
            <p className="mt-8 max-w-2xl text-base sm:text-lg text-slate-400 leading-relaxed">
              Short, practical perspectives on how business development actually works in energy,
              engineering and technical B2B markets, written for the people responsible for growth.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24" data-testid="insights-list">
        <Reveal>
          <Link
            to={`/insights/${lead.slug}`}
            data-testid={`insight-card-${lead.slug}`}
            className="group grid gap-8 border-y border-white/10 py-12 transition-colors duration-300 hover:bg-white/[0.02] lg:grid-cols-[1fr_1.4fr] lg:gap-20 lg:py-16 sm:px-4"
          >
            <div className="flex flex-col justify-between gap-8">
              <div className="flex items-center gap-4 font-mono text-[11px] uppercase tracking-[0.25em]">
                <span className="text-gold">Latest</span>
                <span className="h-px w-8 bg-gold/50" aria-hidden="true" />
                <span className="text-slate-500">{lead.category}</span>
              </div>
              <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500">
                {lead.date} · {lead.readTime}
              </div>
            </div>
            <div>
              <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-[-0.03em] leading-tight text-white transition-colors duration-300 group-hover:text-gold">
                {lead.title}
              </h2>
              <p className="mt-6 max-w-2xl text-base text-slate-400 leading-relaxed">{lead.dek}</p>
              <span className="mt-8 inline-flex items-center gap-2 text-sm font-medium text-electric transition-colors group-hover:text-white">
                Read the commentary
                <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
              </span>
            </div>
          </Link>
        </Reveal>

        <div>
          {rest.map((a, i) => (
            <Reveal key={a.slug} delay={i * 0.05}>
              <Link
                to={`/insights/${a.slug}`}
                data-testid={`insight-card-${a.slug}`}
                className="group grid gap-4 border-b border-white/10 py-10 transition-colors duration-300 hover:bg-white/[0.02] sm:grid-cols-[70px_1fr_auto] sm:items-start sm:gap-8 sm:px-4"
              >
                <span className="font-mono text-sm text-gold/70">{a.num}</span>
                <div>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500">
                    <span className="text-electric/80">{a.category}</span>
                    <span>{a.date}</span>
                    <span>{a.readTime}</span>
                  </div>
                  <h2 className="mt-4 font-display text-xl sm:text-2xl font-bold tracking-[-0.03em] leading-tight text-white transition-colors duration-300 group-hover:text-gold">
                    {a.title}
                  </h2>
                  <p className="mt-3 max-w-2xl text-sm sm:text-base text-slate-400 leading-relaxed">{a.dek}</p>
                </div>
                <ArrowUpRight
                  size={20}
                  className="hidden text-slate-600 transition-all duration-300 group-hover:text-gold group-hover:translate-x-1 group-hover:-translate-y-1 sm:block"
                />
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="border-t border-white/10 py-20 lg:py-28" data-testid="insights-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col items-start gap-8 lg:flex-row lg:items-center lg:justify-between">
          <Reveal>
            <h2 className="font-display max-w-xl text-2xl sm:text-3xl font-bold tracking-[-0.03em] text-white">
              Want this thinking applied to your market?
            </h2>
            <p className="mt-4 max-w-xl text-base text-slate-400 leading-relaxed">
              A short conversation is usually enough to identify where sharper intelligence would
              change your commercial focus.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <a
              href={CALENDLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="insights-book-call-button"
              className="group inline-flex items-center gap-2 bg-gold px-7 py-4 text-[13px] font-semibold uppercase tracking-[0.18em] text-navy transition-all duration-300 hover:bg-gold-glow"
            >
              Book a Call
              <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
            </a>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
